import React, { useEffect, useState, useMemo } from "react";

import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Badge,
  Spinner,
} from "reactstrap";

import { toast } from "react-toastify";
import { Link } from "react-router-dom";

import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const Offers = () => {
  document.title = "HMS | Offers & Promotions";

  /* ======================================
     STATE
  ====================================== */

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  /* ======================================
     INITIAL FORM
  ====================================== */

  const initialForm = {
    name: "",
    code: "",
    description: "",

    offerCategory: "WELCOME",
    offerType: "RESERVATION",

    services: ["HOTEL"],

    discountType: "PERCENTAGE",
    discountValue: "",

    maxDiscountAmount: "",
    minimumBookingAmount: "",

    comboDiscount: "",

    validFrom: "",
    validTo: "",

    usageLimit: "",

    isActive: true,
    isFeatured: false,

    discountRules: [],
  };

  const [formData, setFormData] = useState(initialForm);

  /* ======================================
     MODAL
  ====================================== */

  const toggleModal = () => {
    if (saving) return;

    setModalOpen((prev) => !prev);
  };

  /* ======================================
     FETCH OFFERS
  ====================================== */

  const fetchOffers = async () => {
    try {
      setLoading(true);

      const response = await api.get("/offers");

      console.log("OFFERS RESPONSE:", response.data);

      /*
       Supports both:

       {
         success: true,
         data: []
       }

       OR

       []
      */

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setOffers(data);
    } catch (error) {
      console.error("FETCH OFFERS ERROR:", error);

      toast.error(
        error.response?.data?.message ||
        "Unable to fetch offers"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ======================================
     LOAD OFFERS
  ====================================== */

  useEffect(() => {
    fetchOffers();
  }, []);

  /* ======================================
     ADD OFFER
  ====================================== */

  const handleAdd = () => {
    setEditingOffer(null);

    setFormData({
      ...initialForm,
      services: ["HOTEL"],
      discountRules: [],
    });

    setModalOpen(true);
  };

  /* ======================================
     EDIT OFFER
  ====================================== */

  const handleEdit = (offer) => {
    setEditingOffer(offer);

    setFormData({
      name: offer.name || "",
      code: offer.code || "",
      description: offer.description || "",

      offerCategory:
        offer.offerCategory || "WELCOME",

      offerType:
        offer.offerType || "RESERVATION",

      services:
        Array.isArray(offer.services) &&
          offer.services.length > 0
          ? offer.services
          : [],

      discountType:
        offer.discountType || "PERCENTAGE",

      discountValue:
        offer.discountValue ?? "",

      maxDiscountAmount:
        offer.maxDiscountAmount ?? "",

      minimumBookingAmount:
        offer.minimumBookingAmount ?? "",

      comboDiscount:
        offer.comboDiscount ?? "",

      validFrom: offer.validFrom
        ? new Date(offer.validFrom)
          .toISOString()
          .split("T")[0]
        : "",

      validTo: offer.validTo
        ? new Date(offer.validTo)
          .toISOString()
          .split("T")[0]
        : "",

      usageLimit:
        offer.usageLimit ?? "",

      isActive:
        offer.isActive ?? true,

      isFeatured:
        offer.isFeatured ?? false,

      discountRules:
        offer.discountRules || [],
    });

    setModalOpen(true);
  };

  /* ======================================
     INPUT CHANGE
  ====================================== */

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  /* ======================================
     SERVICE CHANGE
  ====================================== */

  const handleServiceChange = (service) => {
    setFormData((prev) => {
      const exists =
        prev.services.includes(service);

      return {
        ...prev,

        services: exists
          ? prev.services.filter(
            (item) => item !== service
          )
          : [
            ...prev.services,
            service,
          ],
      };
    });
  };

  /* ======================================
     ADD DISCOUNT RULE
  ====================================== */

  const addDiscountRule = () => {
    setFormData((prev) => ({
      ...prev,

      discountRules: [
        ...prev.discountRules,

        {
          label: "",
          minNights: 1,
          discountType: "PERCENTAGE",
          discountValue: "",
          maxDiscountAmount: "",
        },
      ],
    }));
  };

  /* ======================================
     UPDATE DISCOUNT RULE
  ====================================== */

  const updateDiscountRule = (
    index,
    field,
    value
  ) => {
    setFormData((prev) => {
      const rules = [
        ...prev.discountRules,
      ];

      rules[index] = {
        ...rules[index],
        [field]: value,
      };

      return {
        ...prev,
        discountRules: rules,
      };
    });
  };

  /* ======================================
     REMOVE DISCOUNT RULE
  ====================================== */

  const removeDiscountRule = (index) => {
    setFormData((prev) => ({
      ...prev,

      discountRules:
        prev.discountRules.filter(
          (_, i) => i !== index
        ),
    }));
  };

  /* ======================================
     SAVE OFFER
  ====================================== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Offer name is required");
      return;
    }

    if (!formData.code.trim()) {
      toast.error("Offer code is required");
      return;
    }

    if (formData.services.length === 0) {
      toast.error(
        "Please select at least one service"
      );
      return;
    }

    if (
      formData.offerType === "RESERVATION" &&
      formData.discountType === "RULE_BASED" &&
      formData.discountRules.length === 0
    ) {
      toast.error(
        "Please add at least one stay based discount rule"
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name:
          formData.name.trim(),

        code:
          formData.code
            .trim()
            .toUpperCase(),

        description:
          formData.description.trim(),

        offerCategory:
          formData.offerCategory,

        offerType:
          formData.offerType,

        services:
          formData.services,

        discountType:
          formData.discountType,

        discountValue:
          Number(
            formData.discountValue
          ) || 0,

        maxDiscountAmount:
          formData.maxDiscountAmount !== ""
            ? Number(
              formData.maxDiscountAmount
            )
            : null,

        minimumBookingAmount:
          Number(
            formData.minimumBookingAmount
          ) || 0,

        comboDiscount:
          Number(
            formData.comboDiscount
          ) || 0,

        validFrom:
          formData.validFrom,

        validTo:
          formData.validTo,

        usageLimit:
          formData.usageLimit !== ""
            ? Number(
              formData.usageLimit
            )
            : null,

        isActive:
          formData.isActive,

        isFeatured:
          formData.isFeatured,

        discountRules:
          formData.discountRules.map(
            (rule) => ({
              label:
                rule.label || "",

              minNights:
                Number(
                  rule.minNights
                ) || 1,

              discountType:
                rule.discountType ||
                "PERCENTAGE",

              discountValue:
                Number(
                  rule.discountValue
                ) || 0,

              maxDiscountAmount:
                rule.maxDiscountAmount !== ""
                  ? Number(
                    rule.maxDiscountAmount
                  )
                  : null,
            })
          ),
      };

      console.log(
        "SAVING OFFER:",
        payload
      );

      if (editingOffer) {
        await api.put(
          `/offers/${editingOffer._id}`,
          payload
        );

        toast.success(
          "Offer updated successfully"
        );
      } else {
        await api.post(
          "/offers",
          payload
        );

        toast.success(
          "Offer created successfully"
        );
      }

      setModalOpen(false);
      setEditingOffer(null);

      setFormData({
        ...initialForm,
        services: ["HOTEL"],
        discountRules: [],
      });

      await fetchOffers();
    } catch (error) {
      console.error(
        "SAVE OFFER ERROR:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Something went wrong while saving the offer"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ======================================
     DELETE OFFER
  ====================================== */

  const handleDelete = async (id) => {
    const confirmed =
      window.confirm(
        "Are you sure you want to delete this offer?"
      );

    if (!confirmed) return;

    try {
      await api.delete(
        `/offers/${id}`
      );

      toast.success(
        "Offer deleted successfully"
      );

      await fetchOffers();
    } catch (error) {
      console.error(
        "DELETE OFFER ERROR:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Unable to delete offer"
      );
    }
  };

  /* ======================================
     TOGGLE STATUS
  ====================================== */

  const toggleStatus = async (id) => {
    try {
      await api.patch(
        `/offers/${id}/toggle-status`
      );

      toast.success(
        "Offer status updated"
      );

      await fetchOffers();
    } catch (error) {
      console.error(
        "STATUS UPDATE ERROR:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Unable to update offer status"
      );
    }
  };

  /* ======================================
     FORMAT DATE
  ====================================== */

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(
      date
    ).toLocaleDateString("en-IN");
  };

  /* ======================================
     SEARCH
  ====================================== */

  const filteredOffers =
    offers?.filter((offer) => {
      const searchValue =
        search.toLowerCase();

      return (
        offer.name
          ?.toLowerCase()
          .includes(searchValue) ||

        offer.code
          ?.toLowerCase()
          .includes(searchValue) ||

        offer.offerType
          ?.toLowerCase()
          .includes(searchValue) ||

        offer.offerCategory
          ?.toLowerCase()
          .includes(searchValue)
      );
    }) || [];

  /* ======================================
     TABLE COLUMNS
  ====================================== */

  const columns = useMemo(
    () => [
      {
        header: "Offer ID",

        accessorKey: "_id",

        cell: ({ getValue }) =>
          getValue()?.slice(-6),
      },

      {
        header: "Offer",

        accessorKey: "name",

        cell: ({ row }) => (
          <div>
            <strong>
              {row.original.name}
            </strong>

            <br />

            <small className="text-muted">
              {row.original.code}
            </small>
          </div>
        ),
      },

      {
        header: "Category",

        accessorKey: "offerCategory",

        cell: ({ getValue }) => (
          <Badge
            color="primary"
            pill
          >
            {getValue()}
          </Badge>
        ),
      },

      {
        header: "Type",

        accessorKey: "offerType",

        cell: ({ getValue }) => (
          <Badge color="info">
            {getValue()}
          </Badge>
        ),
      },

      {
        header: "Services",

        accessorKey: "services",

        cell: ({ getValue }) => (
          <div className="d-flex flex-wrap gap-1">
            {getValue()?.map(
              (service) => (
                <Badge
                  key={service}
                  color="secondary"
                >
                  {service}
                </Badge>
              )
            )}
          </div>
        ),
      },

      {
        header: "Discount",

        accessorKey: "discountValue",

        cell: ({ row }) => {
          const offer =
            row.original;

          if (
            offer.discountType ===
            "RULE_BASED"
          ) {
            return (
              <Badge color="warning">
                Rule Based
              </Badge>
            );
          }

          if (
            offer.offerType === "COMBO"
          ) {
            return (
              <span>
                {offer.comboDiscount}% OFF
              </span>
            );
          }

          if (
            offer.discountType ===
            "PERCENTAGE"
          ) {
            return (
              <span>
                {offer.discountValue}% OFF
              </span>
            );
          }

          return (
            <span>
              ₹{offer.discountValue} OFF
            </span>
          );
        },
      },

      {
        header: "Validity",

        accessorKey: "validFrom",

        cell: ({ row }) => (
          <div>
            {/* <small>
              {formatDate(
                row.original.validFrom
              )}
            </small>
            <small className="text-muted">
              to
            </small> */}


            <small>
              {formatDate(
                row.original.validTo
              )}
            </small>
          </div>
        ),
      },

      {
        header: "Status",

        accessorKey: "isActive",

        cell: ({ getValue }) => (
          <Badge
            color={
              getValue()
                ? "success"
                : "danger"
            }
          >
            {getValue()
              ? "Active"
              : "Inactive"}
          </Badge>
        ),
      },

      {
        header: "Action",

        cell: ({ row }) => (
          <div className="d-flex justify-content-center align-items-center gap-2">

            {/* EDIT */}

            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();

                handleEdit(
                  row.original
                );
              }}
              className="btn btn-sm btn-soft-primary"
              style={{
                width: "28px",
                height: "28px",
              }}
              title="Edit Offer"
            >
              <i className="ri-edit-line"></i>
            </Link>

            {/* STATUS */}

            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();

                toggleStatus(
                  row.original._id
                );
              }}
              className={
                row.original.isActive
                  ? "btn btn-sm btn-soft-warning"
                  : "btn btn-sm btn-soft-success"
              }
              style={{
                width: "28px",
                height: "28px",
              }}
              title={
                row.original.isActive
                  ? "Disable Offer"
                  : "Enable Offer"
              }
            >
              <i
                className={
                  row.original.isActive
                    ? "ri-toggle-line"
                    : "ri-toggle-fill"
                }
              ></i>
            </Link>

            {/* DELETE */}

            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();

                handleDelete(
                  row.original._id
                );
              }}
              className="btn btn-sm btn-soft-danger"
              style={{
                width: "28px",
                height: "28px",
              }}
              title="Delete Offer"
            >
              <i className="las la-trash-alt"></i>
            </Link>

          </div>
        ),
      },
    ],

    [offers]
  );

  /* ======================================
     RENDER
  ====================================== */

  return (
    <>
      {/* ===============================
          OFFERS TABLE
      ================================ */}

      <Card>

        <CardHeader className="d-flex justify-content-between align-items-center">

          <h5 className="fs-16 mb-0">
            Offers & Promotions
          </h5>

          <div
            className="d-flex align-items-center"
            style={{ gap: "10px" }}
          >

            <Button
              color="primary"
              onClick={handleAdd}
            >
              + Create Offer
            </Button>

          </div>

        </CardHeader>
        <CardBody>

          {loading ? (

            <div className="text-center py-5">

              <Spinner
                size="sm"
                className="me-2"
              />

              Loading offers...

            </div>

          ) : (

            <div
              className="table-responsive"
              style={{
                whiteSpace: "nowrap",
              }}
            >

              <TableContainer
                columns={columns}
                data={filteredOffers}
                customPageSize={10}
                isPagination
                className="text-nowrap"
              />

            </div>

          )}

        </CardBody>

      </Card>


      {/* ===============================
          CREATE / EDIT MODAL
      ================================ */}

      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        size="xl"
      >

        <ModalHeader
          toggle={toggleModal}
        >

          {editingOffer
            ? "Edit Offer"
            : "Create New Offer"}

        </ModalHeader>


        <Form
          onSubmit={handleSubmit}
        >

          <ModalBody>


            {/* ===============================
                NAME + CODE
            ================================ */}

            <Row>

              <Col md="6">

                <FormGroup>

                  <Label>
                    Offer Name *
                  </Label>

                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Example: Summer Bonanza"
                    required
                  />

                </FormGroup>

              </Col>


              <Col md="6">

                <FormGroup>

                  <Label>
                    Offer Code *
                  </Label>

                  <Input
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder="SUMMER50"
                    required
                  />

                </FormGroup>

              </Col>

            </Row>


            {/* DESCRIPTION */}

            <FormGroup>

              <Label>
                Description
              </Label>

              <Input
                type="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe this offer..."
                rows="3"
              />

            </FormGroup>


            {/* CATEGORY + TYPE */}

            <Row>

              <Col md="6">

                <FormGroup>

                  <Label>
                    Offer Category
                  </Label>

                  <Input
                    type="select"
                    name="offerCategory"
                    value={
                      formData.offerCategory
                    }
                    onChange={handleChange}
                  >

                    <option value="WELCOME">
                      Welcome Offer
                    </option>

                    <option value="BONANZA">
                      Bonanza
                    </option>

                    <option value="HOLIDAY">
                      Holiday Offer
                    </option>

                    <option value="FESTIVAL">
                      Festival Offer
                    </option>

                    <option value="SEASONAL">
                      Seasonal Offer
                    </option>

                    <option value="SPECIAL">
                      Special Offer
                    </option>

                  </Input>

                </FormGroup>

              </Col>


              <Col md="6">

                <FormGroup>

                  <Label>
                    Offer Type
                  </Label>

                  <Input
                    type="select"
                    name="offerType"
                    value={
                      formData.offerType
                    }
                    onChange={handleChange}
                  >

                    <option value="RESERVATION">
                      Hotel Reservation
                    </option>

                    <option value="FOOD">
                      Food
                    </option>

                    <option value="TAXI">
                      Taxi
                    </option>

                    <option value="COMBO">
                      Combo Offer
                    </option>

                  </Input>

                </FormGroup>

              </Col>

            </Row>


            {/* SERVICES */}

            <FormGroup>

              <Label className="d-block">
                Applicable Services
              </Label>

              <div className="d-flex flex-wrap gap-3">

                {[
                  "HOTEL",
                  "TAXI",
                  "FOOD",
                  "RESERVATION",
                ].map(
                  (service) => (

                    <FormGroup
                      check
                      key={service}
                    >

                      <Input
                        type="checkbox"
                        checked={
                          formData.services.includes(
                            service
                          )
                        }
                        onChange={() =>
                          handleServiceChange(
                            service
                          )
                        }
                      />

                      <Label check>
                        {service}
                      </Label>

                    </FormGroup>

                  )
                )}

              </div>

            </FormGroup>


            {/* DISCOUNT */}

            <Row>

              <Col md="4">

                <FormGroup>

                  <Label>
                    Discount Type
                  </Label>

                  <Input
                    type="select"
                    name="discountType"
                    value={
                      formData.discountType
                    }
                    onChange={handleChange}
                  >

                    <option value="PERCENTAGE">
                      Percentage
                    </option>

                    <option value="FLAT">
                      Flat Amount
                    </option>

                    {formData.offerType ===
                      "RESERVATION" && (

                        <option value="RULE_BASED">
                          Stay Based Rules
                        </option>

                      )}

                  </Input>

                </FormGroup>

              </Col>


              {formData.discountType !==
                "RULE_BASED" && (

                  <Col md="4">

                    <FormGroup>

                      <Label>
                        Discount Value
                      </Label>

                      <Input
                        type="number"
                        name="discountValue"
                        value={
                          formData.discountValue
                        }
                        onChange={handleChange}
                        min="0"
                      />

                    </FormGroup>

                  </Col>

                )}


              <Col md="4">

                <FormGroup>

                  <Label>
                    Max Discount Amount
                  </Label>

                  <Input
                    type="number"
                    name="maxDiscountAmount"
                    value={
                      formData.maxDiscountAmount
                    }
                    onChange={handleChange}
                    placeholder="Optional"
                    min="0"
                  />

                </FormGroup>

              </Col>

            </Row>


            {/* STAY BASED RULES */}

            {formData.offerType ===
              "RESERVATION" &&

              formData.discountType ===
              "RULE_BASED" && (

                <Card className="border mt-3">

                  <CardBody>

                    <div className="d-flex justify-content-between align-items-center mb-3">

                      <div>

                        <h5 className="mb-1">
                          Stay Based Discount Rules
                        </h5>

                        <small className="text-muted">

                          Example: Book 1 Night =
                          25% OFF, Book 2 Nights =
                          50% OFF

                        </small>

                      </div>


                      <Button
                        type="button"
                        color="primary"
                        size="sm"
                        onClick={
                          addDiscountRule
                        }
                      >

                        + Add Rule

                      </Button>

                    </div>


                    {formData.discountRules
                      .length === 0 && (

                        <p className="text-muted mb-0">

                          No discount rules added yet.

                        </p>

                      )}


                    {formData.discountRules.map(
                      (rule, index) => (

                        <Row
                          key={index}
                          className="mb-2"
                        >

                          <Col md="3">

                            <Input
                              placeholder="Example: 1 Night Offer"
                              value={
                                rule.label
                              }
                              onChange={(e) =>
                                updateDiscountRule(
                                  index,
                                  "label",
                                  e.target.value
                                )
                              }
                            />

                          </Col>


                          <Col md="2">

                            <Input
                              type="number"
                              placeholder="Min Nights"
                              value={
                                rule.minNights
                              }
                              onChange={(e) =>
                                updateDiscountRule(
                                  index,
                                  "minNights",
                                  e.target.value
                                )
                              }
                              min="1"
                            />

                          </Col>


                          <Col md="2">

                            <Input
                              type="select"
                              value={
                                rule.discountType
                              }
                              onChange={(e) =>
                                updateDiscountRule(
                                  index,
                                  "discountType",
                                  e.target.value
                                )
                              }
                            >

                              <option value="PERCENTAGE">
                                Percentage
                              </option>

                              <option value="FLAT">
                                Flat Amount
                              </option>

                            </Input>

                          </Col>


                          <Col md="3">

                            <Input
                              type="number"
                              placeholder="Discount"
                              value={
                                rule.discountValue
                              }
                              onChange={(e) =>
                                updateDiscountRule(
                                  index,
                                  "discountValue",
                                  e.target.value
                                )
                              }
                              min="0"
                            />

                          </Col>


                          <Col md="2">

                            <Button
                              type="button"
                              color="danger"
                              outline
                              onClick={() =>
                                removeDiscountRule(
                                  index
                                )
                              }
                            >

                              <i className="las la-trash-alt me-1"></i>

                              Remove

                            </Button>

                          </Col>

                        </Row>

                      )
                    )}

                  </CardBody>

                </Card>

              )}


            {/* COMBO OFFER */}

            {formData.offerType ===
              "COMBO" && (

                <Card className="border mt-3">

                  <CardBody>

                    <h6>
                      Combo Offer Discount
                    </h6>

                    <p className="text-muted">

                      Example: Book Hotel + Taxi
                      and get 30% OFF.

                    </p>

                    <Row>

                      <Col md="4">

                        <FormGroup>

                          <Label>
                            Combo Discount %
                          </Label>

                          <Input
                            type="number"
                            name="comboDiscount"
                            value={
                              formData.comboDiscount
                            }
                            onChange={
                              handleChange
                            }
                            placeholder="Example: 30"
                            min="0"
                            max="100"
                          />

                        </FormGroup>

                      </Col>

                    </Row>

                  </CardBody>

                </Card>

              )}


            {/* VALIDITY */}

            <Row className="mt-3">

              <Col md="4">

                <FormGroup>

                  <Label>
                    Valid From *
                  </Label>

                  <Input
                    type="date"
                    name="validFrom"
                    value={
                      formData.validFrom
                    }
                    onChange={handleChange}
                    required
                  />

                </FormGroup>

              </Col>


              <Col md="4">

                <FormGroup>

                  <Label>
                    Valid To *
                  </Label>

                  <Input
                    type="date"
                    name="validTo"
                    value={
                      formData.validTo
                    }
                    onChange={handleChange}
                    required
                  />

                </FormGroup>

              </Col>


              <Col md="4">

                <FormGroup>

                  <Label>
                    Usage Limit
                  </Label>

                  <Input
                    type="number"
                    name="usageLimit"
                    value={
                      formData.usageLimit
                    }
                    onChange={handleChange}
                    placeholder="Unlimited"
                    min="0"
                  />

                </FormGroup>

              </Col>

            </Row>


            {/* STATUS */}

            <Row className="mt-2">

              <Col md="3">

                <FormGroup check>

                  <Input
                    type="checkbox"
                    name="isActive"
                    checked={
                      formData.isActive
                    }
                    onChange={handleChange}
                  />

                  <Label check>
                    Active Offer
                  </Label>

                </FormGroup>

              </Col>


              <Col md="3">

                <FormGroup check>

                  <Input
                    type="checkbox"
                    name="isFeatured"
                    checked={
                      formData.isFeatured
                    }
                    onChange={handleChange}
                  />

                  <Label check>
                    Featured Offer
                  </Label>

                </FormGroup>

              </Col>

            </Row>

          </ModalBody>


          <ModalFooter>

            <Button
              type="button"
              color="secondary"
              onClick={toggleModal}
              disabled={saving}
            >

              Cancel

            </Button>


            <Button
              type="submit"
              color="primary"
              disabled={saving}
            >

              {saving && (

                <Spinner
                  size="sm"
                  className="me-2"
                />

              )}

              {editingOffer
                ? "Update Offer"
                : "Create Offer"}

            </Button>

          </ModalFooter>

        </Form>

      </Modal>

    </>
  );
};

export default Offers;